import type { FC, PropsWithChildren } from 'react';

import { useAppDispatch } from '../../../store/react_redux_typed';
import type { AppDispatch, GetState } from '../../../store/types';
import ControlsModal, { type NotFormProps } from '../controls_modal';

type ConfirmActionWithId = (
  id: string,
) => (dispatch: AppDispatch, getState: GetState) => Promise<unknown>;

type ConfirmAction = () => (
  dispatch: AppDispatch,
  getState: GetState,
) => Promise<unknown>;

export type PromiseConfirmModalProps = {
  id?: string;
  ids?: string[];
  confirmAction: ConfirmActionWithId | ConfirmAction;
};

// This modal always renders the non-form arm: its onConfirm runs after the
// dispatched action settles and never receives form data.
type Props = PromiseConfirmModalProps & PropsWithChildren<NotFormProps>;

const PromiseConfirmModal: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { children, id, ids, confirmAction, onConfirm, ...restProps } = props;
  return (
    <ControlsModal
      {...restProps}
      onConfirm={() => {
        if (id) {
          const action = confirmAction(id);
          return dispatch(action).then(onConfirm);
        }
        if (ids) {
          const promises = ids.map((itemId) => dispatch(confirmAction(itemId)));
          return Promise.all(promises).then(onConfirm);
        }

        // Neither id nor ids was given, so the caller must have passed the
        // no-argument shape. Props do not tie the two together; making
        // PromiseConfirmModalProps a discriminated union on id/ids/neither
        // would, at the cost of checking all 19 call sites.
        // biome-ignore lint/plugin: see above.
        const action = (confirmAction as ConfirmAction)();
        return dispatch(action).then(onConfirm);
      }}
    >
      {children}
    </ControlsModal>
  );
};

export default PromiseConfirmModal;
